import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import Stack from '@/components/Stack';
import { Small } from '@/components/Typography';
import { axiosRubyClient } from '@/util/axios/axiosClient';

type Props = {
  open: boolean;
  workerId: number;
  skillId: number;
  skillRequestId?: number;
  experienceId?: number | null;
  onClose: () => void;
  onSuccess: () => void;
};

const AddWorkExperienceModal = ({
  open,
  workerId,
  skillId,
  skillRequestId,
  experienceId,
  onClose,
  onSuccess,
}: Props) => {
  const [companyName, setCompanyName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isStaffingAgency, setIsStaffingAgency] = useState(false);
  const [isCurrent, setIsCurrent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fetchingData, setFetchingData] = useState(false);

  useEffect(() => {
    if (open && experienceId) {
      // Fetch experience data when editing
      const fetchExperience = async () => {
        setFetchingData(true);
        try {
          const response = await axiosRubyClient.get(
            `/api/admin/work_experiences/${experienceId}`,
          );

          if (response.data.success) {
            const exp = response.data.data;
            setCompanyName(exp.company_name || '');
            setStartDate(exp.start_date ? exp.start_date.split('T')[0] : '');
            setEndDate(exp.end_date ? exp.end_date.split('T')[0] : '');
            setIsStaffingAgency(exp.is_staffing_agency || false);
            setIsCurrent(!exp.end_date);
          }
        } catch (err) {
          console.error('Error fetching work experience:', err);
          setError('Failed to load work experience data');
        } finally {
          setFetchingData(false);
        }
      };

      fetchExperience();
    } else if (open && !experienceId) {
      // Reset form for new experience
      setCompanyName('');
      setStartDate('');
      setEndDate('');
      setIsStaffingAgency(false);
      setIsCurrent(false);
      setError(null);
    }
  }, [open, experienceId]);

  const handleClose = () => {
    if (!loading) {
      setCompanyName('');
      setStartDate('');
      setEndDate('');
      setIsStaffingAgency(false);
      setIsCurrent(false);
      setError(null);
      onClose();
    }
  };

  const handleSubmit = async () => {
    setError(null);

    // Validation
    if (!companyName.trim()) {
      setError('Company name is required');
      return;
    }

    if (!startDate) {
      setError('Start date is required');
      return;
    }

    if (!isCurrent && !endDate) {
      setError('End date is required (or check "Currently working here")');
      return;
    }

    if (endDate && new Date(endDate) < new Date(startDate)) {
      setError('End date must be after start date');
      return;
    }

    setLoading(true);

    try {
      const payload: any = {
        worker_id: workerId,
        company_name: companyName.trim(),
        start_date: startDate,
        end_date: isCurrent ? null : endDate || null,
        is_staffing_agency: isStaffingAgency,
      };

      // Add skill_id and skill_request_id for create operations
      if (!experienceId) {
        payload.skill_id = skillId;
        if (skillRequestId) {
          payload.skill_request_id = skillRequestId;
        }
      }

      let response;
      if (experienceId) {
        // Update existing experience
        response = await axiosRubyClient.patch(
          `/api/admin/work_experiences/${experienceId}`,
          payload,
        );
      } else {
        // Create new experience
        response = await axiosRubyClient.post(
          '/api/admin/work_experiences',
          payload,
        );
      }

      if (response.data.success) {
        onSuccess();
        handleClose();
      } else {
        setError(
          response.data.errors?.join(', ') ||
            `Failed to ${experienceId ? 'update' : 'add'} work experience`,
        );
      }
    } catch (err: any) {
      console.error(
        `Error ${experienceId ? 'updating' : 'adding'} work experience:`,
        err,
      );
      setError(
        err.response?.data?.errors?.join(', ') ||
          err.response?.data?.error ||
          `Failed to ${experienceId ? 'update' : 'add'} work experience`,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {experienceId ? 'Edit Work Experience' : 'Add Work Experience'}
      </DialogTitle>
      <DialogContent>
        <Stack vertical gap={16} style={{ marginTop: '8px' }}>
          <TextField
            label="Company Name"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            fullWidth
            required
            disabled={loading}
            placeholder="e.g., Hilton Hotel"
          />

          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            fullWidth
            required
            disabled={loading}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            fullWidth
            disabled={loading || isCurrent}
            InputLabelProps={{ shrink: true }}
            helperText={isCurrent ? 'Currently working here' : ''}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={isCurrent}
                onChange={(e) => {
                  setIsCurrent(e.target.checked);
                  if (e.target.checked) {
                    setEndDate('');
                  }
                }}
                disabled={loading}
              />
            }
            label="I currently work here"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={isStaffingAgency}
                onChange={(e) => setIsStaffingAgency(e.target.checked)}
                disabled={loading}
              />
            }
            label="This was through a staffing agency"
          />

          {error && (
            <Small color="danger" style={{ marginTop: '8px' }}>
              {error}
            </Small>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || fetchingData}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading
            ? experienceId
              ? 'Updating...'
              : 'Adding...'
            : experienceId
              ? 'Update Experience'
              : 'Add Experience'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddWorkExperienceModal;
